import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { AccessStatus, UserRole } from '@/modules/api/codegen';
import type { SelectInputProps } from '@/modules/forms';
import { useGroups } from '@/modules/groups';
import type { UserAdministrationFilters } from '@/modules/user-administration/hooks';

const allValue = 'all';

export const useFilterSelectInputProps = () => {
    const { formatMessage } = useIntl();

    const {
        dataWithoutAllUsersGroup: groups,
        handleLoadMore: handleLoadMoreGroups,
        api: groupsApi,
        handleSearch: handleSearchGroups,
    } = useGroups();

    const handleResetGroupsSearch = useCallback(() => {
        handleSearchGroups('');
    }, [handleSearchGroups]);

    const filterSelectInputProps = {
        status: {
            options: ([allValue, ...Object.values(AccessStatus)] as const).map(value => ({
                value,
                label: formatMessage({
                    id: value === allValue ? 'user-administration.filters.value.all' : `user-administration.status.${value}`,
                }),
            })),
        },
        groups: {
            options: ([allValue, ...groups] as const).map(group => ({
                value: typeof group === 'object' ? group.id : group,
                label: group === allValue ? formatMessage({ id: 'user-administration.filters.value.all' }) : `${group.name}`,
            })),
            loading: groupsApi.inProgress,
            loadingMore: groupsApi.inProgressNext,
            hasMore: Boolean(groupsApi.nextPageToken),
            loadMore: handleLoadMoreGroups,
            showSearch: {
                filterOption: false,
                autoClearSearchValue: true,
                onSearch: handleSearchGroups,
            },
            onBlur: handleResetGroupsSearch,
            onSelect: handleResetGroupsSearch,
        },
        role: {
            options: ([allValue, ...Object.values(UserRole)] as const).map(value => ({
                value,
                label: formatMessage({
                    id: value === allValue ? 'user-administration.filters.value.all' : `user-administration.role.${value}`,
                }),
            })),
        },
    } as const satisfies Record<
        Exclude<keyof UserAdministrationFilters, 'search'>,
        Partial<
            Pick<SelectInputProps, 'options' | 'loadMore' | 'loading' | 'loadingMore' | 'hasMore' | 'showSearch' | 'onBlur' | 'onSelect'>
        >
    >;

    return filterSelectInputProps;
};
